// src/components/survey/SatisfactionSurveyModal.jsx

import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui';

const PROMPT_INTERVAL_DAYS = 30;

const RATING_OPTIONS = [
  { value: 1, emoji: '😢', label: 'Very unsatisfied' },
  { value: 2, emoji: '🙁', label: 'Unsatisfied' },
  { value: 3, emoji: '😐', label: 'Neutral' },
  { value: 4, emoji: '🙂', label: 'Satisfied' },
  { value: 5, emoji: '😄', label: 'Very satisfied' },
];

// Whether the user is due for the satisfaction prompt (patients only, every 30 days)
function isSurveyDue(user) {
  const isPatient = user?.patient_id !== null && user?.patient_id !== undefined;
  if (!isPatient) return false;

  const aclCare = user?.acl_care;
  if (!aclCare?.acl_onboarding_completed) return false;

  const lastDate = aclCare.satisfaction_last_prompted_at || aclCare.acl_onboarding_completed_at;
  if (!lastDate) return true;

  const daysSince = (Date.now() - new Date(lastDate).getTime()) / (1000 * 60 * 60 * 24);
  return daysSince >= PROMPT_INTERVAL_DAYS;
}

export function SatisfactionSurveyModal() {
  const { user, aclCare, updateAclCare } = useAuth();

  const [rating, setRating] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || !isSurveyDue(user)) return null;

  const handleSubmit = async () => {
    if (!rating || loading) return;

    setLoading(true);
    const now = new Date().toISOString();

    await updateAclCare({
      satisfaction_surveys: [
        ...(aclCare?.satisfaction_surveys || []),
        { rating, submitted_at: now },
      ],
      satisfaction_last_prompted_at: now,
    });

    setDismissed(true);
  };

  const handleDismiss = async () => {
    if (loading) return;
    setDismissed(true);
    // Record the prompt so the user isn't asked again for another 30 days
    await updateAclCare({
      satisfaction_last_prompted_at: new Date().toISOString(),
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 transition-opacity" />

      {/* Modal */}
      <div className="flex min-h-full items-end justify-center p-4 sm:items-center sm:p-0">
        <div className="relative bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-md transform transition-all p-6">
          <div className="text-center mb-6">
            <h2 className="text-lg font-semibold text-gray-900">
              How satisfied are you with this app?
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Your feedback helps us improve ACL Care
            </p>
          </div>

          <div className="flex justify-between gap-2 mb-6">
            {RATING_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setRating(option.value)}
                aria-label={option.label}
                title={option.label}
                className={`
                  flex-1 py-3 rounded-lg border-2 text-3xl transition-colors
                  ${rating === option.value
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-200 hover:border-gray-300'
                  }
                `}
              >
                {option.emoji}
              </button>
            ))}
          </div>

          <Button
            onClick={handleSubmit}
            fullWidth
            loading={loading}
            disabled={!rating || loading}
          >
            Submit
          </Button>

          <button
            onClick={handleDismiss}
            className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Ask me later
          </button>
        </div>
      </div>
    </div>
  );
}
